import { ButtonType, IconButton } from '@ardoq/button';
import { colors, s8, s16, shadowS, space } from '@ardoq/design-tokens';
import { TextArea } from '@ardoq/forms';
import { IconName } from '@ardoq/icons';
import { FlexBox } from '@ardoq/layout';
import styled from 'styled-components';
import { PeekNotificationBanner, StatusType, Tag } from '@ardoq/status-ui';
import { t, type I18nLocale } from '@ardoq/i18n';
import type { AssistantBanner, HistoryPlacement } from '../../assistantTypes';
import { StackedBanners } from '../StackedBanners';
import {
  type AssistantDocumentsProps,
  DocumentChip,
  GiveContextButton,
} from '../../documents/components';
import { useEffect, useRef } from 'react';
import {
  AssistantConfigSelect,
  type AssistantConfigSelectProps,
} from '../../experimental/assistant-config';

export const CHAT_INPUT_ANCHOR_ID = 'assistant-chat-input-anchor';

export type ChatFooterProps = {
  input: string;
  onInputChange: (value: string) => void;
  sendMessage: () => void;
  cancelMessage?: () => void;
  isCancelRequestPending: boolean;
  isRequestPending?: boolean;
  isInputBlocked?: boolean;
  isEditingPlan?: boolean;
  className?: string;
  pageName?: string;
  pageIcon?: IconName;
  dataScopeTooltip?: string;
  placeholder: string;
  historyPlacement: HistoryPlacement;
  documents?: AssistantDocumentsProps;
  canSendMessage: boolean;
  i18nLocale: I18nLocale;
  hasPendingInputFocusRequest?: boolean;
  banners?: AssistantBanner[];
  onDismissBanner?: (banner: AssistantBanner) => void;
  onConsumedInputFocusRequest?: (retain: boolean) => void;
  assistantConfigInternal?: Omit<
    AssistantConfigSelectProps,
    'isDisabled' | 'i18nLocale'
  >;
};

export const ChatFooter = ({
  input,
  placeholder,
  onInputChange,
  sendMessage,
  cancelMessage,
  isCancelRequestPending,
  isRequestPending = false,
  isInputBlocked = false,
  isEditingPlan = false,
  className,
  pageName,
  pageIcon,
  dataScopeTooltip,
  i18nLocale,
  documents,
  canSendMessage,
  hasPendingInputFocusRequest = false,
  banners = [],
  onDismissBanner,
  onConsumedInputFocusRequest,
  assistantConfigInternal,
}: ChatFooterProps) => {
  const {
    giveContext,
    selectedDocuments = [],
    onRemoveDocument,
    webSearchEnabled = false,
    onToggleWebSearch,
  }: Partial<AssistantDocumentsProps> = documents ?? {};
  const showSelectedDocuments = selectedDocuments.length > 0;
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const textarea = textareaRef.current;

    if (!hasPendingInputFocusRequest || !textarea) {
      return;
    }

    textarea.focus({ preventScroll: true });
    onConsumedInputFocusRequest?.(false);
  }, [hasPendingInputFocusRequest, onConsumedInputFocusRequest]);

  return (
    <Anchor className={className}>
      <PeekNotificationBanner isVisible={isEditingPlan} status="info">
        {t(
          i18nLocale,
          'assistant:planningMode.describeChanges',
          'Describe your changes to the plan'
        )}
      </PeekNotificationBanner>
      <StackedBanners
        banners={banners}
        onDismiss={onDismissBanner}
        i18nLocale={i18nLocale}
      />
      <Container id={CHAT_INPUT_ANCHOR_ID} $isInputBlocked={isInputBlocked}>
        {showSelectedDocuments && (
          <DocumentChipsRow gap="small" paddingBottom="small">
            {selectedDocuments.map(item => (
              <DocumentChip
                key={item.id}
                item={item}
                i18nLocale={i18nLocale}
                onRemove={() => onRemoveDocument?.(item)}
              />
            ))}
          </DocumentChipsRow>
        )}
        <MessageTextareaWrapper>
          <MessageTextarea
            ref={textareaRef}
            rows={1}
            value={input}
            isDisabled={isInputBlocked}
            onValueChange={onInputChange}
            onKeyDown={e => {
              const shiftOrControl = e.shiftKey || e.ctrlKey;
              if (e.key === 'Enter' && !shiftOrControl) {
                e.preventDefault();
                sendMessage();
              }
            }}
            aria-label={t(
              i18nLocale,
              'assistant:ariaLabels.chatInput',
              'Assistant Chat Input'
            )}
            placeholder={placeholder}
            autoComplete="off"
            autoFocus
            onFocus={e => {
              // Toggle value to put cursor at end
              const value = e.target.value;
              e.target.value = '';
              e.target.value = value;
            }}
          />
        </MessageTextareaWrapper>
        <Footerfooter>
          <FooterLeft>
            {giveContext && (
              <GiveContextButton
                {...giveContext}
                isRequestPending={isRequestPending}
                isDisabled={isInputBlocked || isEditingPlan}
              />
            )}
            {webSearchEnabled && (
              <Tag
                leftIcon={{ name: IconName.GLOBE }}
                isDisabled={isRequestPending}
                onCloseClick={
                  isRequestPending
                    ? undefined
                    : () => onToggleWebSearch?.(false)
                }
                statusType={StatusType.INFO}
                style={{
                  paddingTop: 2,
                  paddingBottom: 2,
                }}
              >
                <TagLabel $color={colors.textInfo} $fontWeight={400}>
                  {t(i18nLocale, 'assistant:webSearch.pillLabel', 'Search')}
                </TagLabel>
              </Tag>
            )}
            {pageName && (
              <Tag
                leftIcon={pageIcon ? { name: pageIcon } : undefined}
                isDisabled={isInputBlocked}
                style={{
                  ...(!isInputBlocked && {
                    backgroundColor: colors.white950,
                    borderColor: colors.grey300,
                  }),
                  paddingTop: 2,
                  paddingBottom: 2,
                }}
                data-tooltip-text={dataScopeTooltip}
              >
                <TagLabel>{pageName}</TagLabel>
              </Tag>
            )}
          </FooterLeft>
          {isRequestPending && cancelMessage ? (
            <IconButton
              onClick={cancelMessage}
              iconName={IconName.SQUARE}
              buttonType={ButtonType.PRIMARY}
              label={t(
                i18nLocale,
                'assistant:actions.stopGenerating',
                'Stop generating'
              )}
              isLoading={isCancelRequestPending}
              isDisabled={isCancelRequestPending}
              i18nLocale={i18nLocale}
            />
          ) : (
            <IconButton
              onClick={sendMessage}
              isLoading={isRequestPending}
              isDisabled={isRequestPending || !canSendMessage}
              iconName={IconName.ARROW_UPWARD}
              buttonType={ButtonType.PRIMARY}
              label={t(
                i18nLocale,
                'assistant:actions.sendMessage',
                'Send message'
              )}
              i18nLocale={i18nLocale}
            />
          )}
        </Footerfooter>
        {assistantConfigInternal && (
          <AssistantConfigSelect
            {...assistantConfigInternal}
            i18nLocale={i18nLocale}
            isDisabled={isRequestPending}
          />
        )}
      </Container>
    </Anchor>
  );
};

const Anchor = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin: ${space.small};
`;

const Container = styled.div<{ $isInputBlocked: boolean }>`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 0;
  background-color: ${({ $isInputBlocked }) =>
    $isInputBlocked ? colors.surfaceDisabled : colors.surfaceDefault};
  border: 1px solid
    ${({ $isInputBlocked }) =>
      $isInputBlocked ? colors.borderAction : colors.borderSubtle};
  border-radius: 8px;
  box-shadow: ${shadowS};
  padding: ${space.small};

  &:focus-within {
    border-color: ${colors.borderActionActive};
    box-shadow:
      ${shadowS},
      inset 0 0 0 1px ${colors.borderActionActive};
  }
`;

const DocumentChipsRow = styled(FlexBox)`
  width: 100%;
  overflow-x: auto;
`;

const MessageTextareaWrapper = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  width: 100%;
`;

const MessageTextarea = styled(TextArea)`
  width: 100%;
  border: none !important;
  box-shadow: none !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
  padding-top: 0 !important;
  color: ${colors.textDefault};

  &:disabled {
    background-color: transparent;
    padding-right: ${s16} !important;
  }

  &:disabled::placeholder {
    color: ${colors.textModerate};
  }
`;

const Footerfooter = styled.div`
  display: flex;
  flex-shrink: 0;
  justify-content: space-between;
  align-items: flex-end;
  max-width: 100%;
  width: 100%;
  margin-top: ${s8};
  gap: ${s8};
  div {
    overflow: hidden;
  }
`;

const FooterLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${s8};
  min-width: 0;
`;

const TagLabel = styled.span<{ $color?: string; $fontWeight?: number }>`
  color: ${({ $color }) => $color || colors.grey600};
  font-size: 12px;
  font-weight: ${({ $fontWeight }) => $fontWeight || 300};
  display: block;
  max-width: calc(100%);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
